import AccountCircle from '@mui/icons-material/AccountCircleOutlined';
import AppsIcon from '@mui/icons-material/Apps';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, CircularProgress, Menu, Paper, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';
import { AppBar, Button, Logout, UserMenu, useGetIdentity } from 'react-admin';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import Logo from '../components/Logo/Logo';
import { useAppContext } from '../contexts/AppContext';
import { QualityIcon } from '../resources/quality';
import { customPalette } from '../themes/customTheme';
import { hasRole } from '../utils/roles';

const ContactSupportIcon = () => (
  <SvgIcon>
    <path
      d="M21.4 7.0002H19.9C19.4 3.2002 16 0.200195 12 0.200195C8 0.200195 4.6 3.2002 4.1 7.0002H2.5C1.4 7.0002 0.5 7.9002 0.5 9.0002V15.0002C0.5 16.1002 1.4 17.0002 2.5 17.0002H6.5V7.0002H5.6C6.1 4.0002 8.8 1.8002 12 1.8002C15.2 1.8002 17.9 4.0002 18.4 7.0002H17.4V17.0002H19.2C19.2 19.0002 17.8 20.7002 15.9 21.1002C15.6 20.2002 14.8 19.5002 13.7 19.5002H11.2C10 19.5002 9 20.5002 9 21.7002C9 22.9002 10 23.9002 11.2 23.9002H13.7C14.6 23.9002 15.4 23.4002 15.7 22.6002C18.5 22.2002 20.7 19.8002 20.7 16.9002H21.4C22.5 16.9002 23.4 16.0002 23.4 14.9002V9.0002C23.4 7.9002 22.5 7.0002 21.4 7.0002ZM5 15.5002H2.5C2.2 15.5002 2 15.3002 2 15.0002V9.0002C2 8.7002 2.2 8.5002 2.5 8.5002H5V15.5002ZM13.8 22.5002H11.3C10.9 22.5002 10.5 22.2002 10.5 21.7002C10.5 21.2002 10.8 20.9002 11.3 20.9002H13.8C14.2 20.9002 14.6 21.2002 14.6 21.7002C14.6 22.2002 14.2 22.5002 13.8 22.5002ZM21.9 15.0002C21.9 15.3002 21.7 15.5002 21.4 15.5002H18.9V8.5002H21.4C21.7 8.5002 21.9 8.7002 21.9 9.0002V15.0002Z"
      fill="#737373"
    />
  </SvgIcon>
);

const useStyles = makeStyles({
  title: {
    flex: 1,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  spacer: {
    flex: 1,
  },
  logo: {
    height: '40px',
    width: '145px',
    marginBottom: 5,
    marginTop: 5,
  },
  appbar: {
    color: '#737373',
    position: 'absolute',
    boxShadow: 'none',
  },
});

const CustomUserMenu = () => {
  const { isLoading: identityLoading } = useGetIdentity();

  if (identityLoading)
    return (
      <Box
        sx={{
          minWidth: '140px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={18} thickness={5} sx={{ color: 'primary' }} />
      </Box>
    );

  return (
    <UserMenu>
      <Button
        component={Link}
        to="/profile"
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '6px 14px',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: '2px' }}>
          <AccountCircle sx={{ color: '#757575' }} />
          <Typography
            sx={{
              color: customPalette.greyDark,
              fontWeight: 400,
              fontSize: '16px !important',
              lineHeight: 1.5,
              marginLeft: '14px',
            }}
          >
            Perfil
          </Typography>
        </Box>
      </Button>
      <Logout />
    </UserMenu>
  );
};

const AppsMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { switchApp, getAvailableApps } = useAppContext();
  const open = Boolean(anchorEl);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAppSwitch = appType => {
    switchApp(appType);
    handleClose();
  };

  const availableApps = getAvailableApps();
  return (
    <>
      <Tooltip title="Cambiar aplicación">
        <IconButton
          onClick={handleClick}
          aria-label="apps menu"
          sx={{ color: '#737373' }}
        >
          <AppsIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            padding: 2,
            minWidth: 280,
            maxWidth: 320,
            maxHeight: '80vh',
            overflow: 'auto',
          },
        }}
        disableScrollLock={true}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns:
                availableApps?.length === 1
                  ? 'repeat(auto-fit, 1fr)'
                  : 'repeat(2, 1fr)',
              gap: 2,
            }}
          >
            {/* Renderizar aplicaciones disponibles dinámicamente */}
            {availableApps.map(appType => {
              const appConfig = {
                card: {
                  name: 'Scuore Card',
                  icon: <Icon variant="card" />,
                  color: '#8955F2',
                },
                quality: {
                  name: 'Scuore Quality',
                  icon: <QualityIcon sx={{ color: 'white', fontSize: 28 }} />,
                  color: '#3F49DB',
                },
              };

              const config = appConfig[appType];
              if (!config) return null;

              return (
                <Paper
                  key={appType}
                  elevation={0}
                  onClick={() => handleAppSwitch(appType)}
                  sx={{
                    padding: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: '#f1f3f4',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    minHeight: 100,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: config.color,
                      borderRadius: '50%',
                      padding: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {config.icon}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#202124',
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: 1.2,
                    }}
                  >
                    {config.name}
                  </Typography>
                </Paper>
              );
            })}
          </Box>
        </Box>
      </Menu>
    </>
  );
};

// Componente Logo dinámico según la aplicación
const DynamicLogo = () => {
  const [currentApp, setCurrentApp] = useState(
    () => localStorage.getItem('currentApp') || 'card'
  );

  useEffect(() => {
    const handleStorageChange = () => {
      setCurrentApp(localStorage.getItem('currentApp') || 'card');
    };

    // Escuchar cambios en localStorage
    window.addEventListener('storage', handleStorageChange);

    // También detectar cambios locales (en la misma pestaña)
    const checkCurrentApp = () => {
      const newApp = localStorage.getItem('currentApp') || 'card';
      if (newApp !== currentApp) {
        setCurrentApp(newApp);
      }
    };

    const interval = setInterval(checkCurrentApp, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [currentApp]);

  if (currentApp === 'quality') {
    return <Logo variant="quality" />;
  }

  // Logo por defecto para Scuore Card
  return <Logo />;
};

export const CustomAppBar = () => {
  const classes = useStyles();
  return (
    <AppBar
      color="inherit"
      userMenu={<CustomUserMenu />}
      className={classes.appbar}
    >
      <Typography
        variant="h6"
        color="inherit"
        className={classes.title}
        id="react-admin-title"
      />
      <Box
        sx={{
          display: 'flex',
          position: { xs: 'static', sm: 'absolute' },
          left: { xs: '150px', sm: '50%' },
          transform: { xs: 'none', sm: 'translate(-50%, 50%)' },
          height: '100%',
          bottom: '24px',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Link to="/" style={{ display: 'flex' }}>
          <DynamicLogo />
        </Link>
      </Box>
      <span className={classes.spacer} />
      <AppsMenu />
      {hasRole(['admin', 'gerente']) && (
        <Tooltip title="Gestión de Usuarios">
          <IconButton
            component={Link}
            to="/user-management"
            aria-label="user management"
            sx={{ color: '#737373' }}
          >
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      )}
      <Link to={'/contacto/create'}>
        <Tooltip title={'Asistencia técnica'}>
          <IconButton aria-label={'Asistencia técnica'}>
            <ContactSupportIcon />
          </IconButton>
        </Tooltip>
      </Link>
    </AppBar>
  );
};
